/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PasswordInput from '../user/PasswordInput';
import {
  useHideHeader,
  useHideHeaderButton,
  useLoading,
  useIsAuthorized,
  useUserInfo,
  useStation,
  useImpersonatedUserId
} from '../contexts/GlobalStateContext';
import ChangePassword from '../user/ChangePassword';
import BattenburgLoader from '../common/BattenburgLoader';
import { fetchWithAuth } from '../services/api';

const LoginOutForm = () => {
  const { isAuthorized, setIsAuthorized } = useIsAuthorized();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captionText, setCaptionText] = useState('Logga in');
  const { loading, setLoading } = useLoading();
  const { setHideHeader } = useHideHeader();
  const { setHideHeaderButton } = useHideHeaderButton();
  const { userInfo, setUserInfo } = useUserInfo();
  const { setImpersonatedUserId } = useImpersonatedUserId();
  const navigate = useNavigate();


  useEffect(() => {
    setHideHeaderButton(true)
    setHideHeader(true)
  },[])
  const checkPasswordOnServerAsync = async (password, username) => {
    try {
      const response = await fetch('/api/authorization/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
          password: password
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Login error:', error);
        return null;
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Network error:', error);
      throw error;
    }
  }

  const checkPasswordAsync = async (password, username) => {
    setLoading(true);
    try {
      const response = await checkPasswordOnServerAsync(password, username);

      if (response && response.token) {
        // Spara i sessionStorage
        sessionStorage.setItem('jwt_token', response.token);
        sessionStorage.setItem('user_id', response.userId);
        sessionStorage.setItem('username', response.username);
        sessionStorage.setItem('email', response.email);
        sessionStorage.setItem('user_role', response.role || 'Standard');

        // Uppdatera state
        setIsAuthorized(true);
        setUserInfo({
          userId: response.userId,
          username: response.username,
          email: response.email,
          role: response.role || 'Standard'
        });

        setCaptionText('Logga in');
        setUsername('');
        setPassword('');

        // Navigera DIREKT här baserat på roll
        if (response.role === 'Standard') {
          setHideHeaderButton(true)
          setHideHeader(true)
          navigate("/scheman");
        } else {
          navigate('/'); // Admin/SystemAdmin går till startsidan
          setHideHeaderButton(false)
          setHideHeader(false)
        }

        return 'PasswordOk';
      } else {
        setCaptionText('Fel användarnamn eller lösenord');
        return 'PasswordIncorrect';
      }
    } catch (error) {
      console.error(error);
      setCaptionText('Inloggning misslyckades. Försök igen.');
      return 'PasswordIncorrect';
    } finally {
      setLoading(false);
    }
  }

  const logOutUserAsync = async () => {
    const response = await fetchWithAuth('/api/authorization/logout', {
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error('Logout failed');
    }

    const data = await response.json();
    return data;
  }

  const logOutAsync = async () => {
    setLoading(true);
    try {
      const data = await logOutUserAsync();

      if (data.success) {
        sessionStorage.clear();

        // Rensa state
        setImpersonatedUserId("")
        setUserInfo(null);
        navigate('/');
        setTimeout(() => {
          setIsAuthorized(false);
        }, 20)

        return 'userLoggedOut';
      } else {
        alert(`Unexpected server response: ${data.message}`);
        return 'Error';
      }
    } catch (error) {
      console.error(error);
      // Även om servern failar, logga ut lokalt
      sessionStorage.clear();
      setIsAuthorized(false);
      setUserInfo(null);
      return 'Error';
    } finally {
      setLoading(false);
    }
  }

  const handleClose = () => {
    setUsername('');
    setPassword('');
    setCaptionText('Logga in');
  }

  const handleLogInOut = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setLoading(true);

    try {
      if (isAuthorized) {
        const response = await logOutAsync();
        if (response === 'userLoggedOut' || response === 'userAlreadyLoggedOut') {
          handleClose();
          navigate('/');
        }
      } else {
        if (!username.trim()) {
          setCaptionText('Ange användarnamn');
          setLoading(false);
          return;
        }
        if (!password) {
          setCaptionText('Ange lösenord');
          setLoading(false);
          return;
        }

        const response = await checkPasswordAsync(password, username);
        if (response === 'PasswordOk') {
          handleClose();
        }
      }
    } catch (error) {
      console.error("Error:", error);
      setCaptionText('Ett fel uppstod. Försök igen.');
    } finally {
      setLoading(false);
    }
  }

  const getRoleBadge = (role) => {
    const badges = {
      'SystemAdmin': <span className="tw-ml-2 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-rounded tw-bg-red-600 tw-text-white">SystemAdmin</span>,
      'Admin': <span className="tw-ml-2 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-rounded tw-bg-blue-600 tw-text-white">Admin</span>,
      'Standard': <span className="tw-ml-2 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-rounded tw-bg-gray-600 tw-text-white">Standard</span>
    }
    return badges[role] || null;
  }

  return (
    <div className="container" style={{ width: '305px' }}>
      <div className="row-height">
        <div className="col-md-height" >
          <div className="p-1" />
          <form onSubmit={handleLogInOut}>
            <div className="tw-flex tw-flex-col">
              <div className="tw-text-lg tw-font-semibold" id="modal-title">
                {!isAuthorized ? (
                  <>
                    <PasswordInput
                      text={username}
                      type="text"
                      placeholder="Användarnamn"
                      preText={captionText}
                      onTextChanged={setUsername}
                      autoFocus
                    />
                    <PasswordInput
                      text={password}
                      type="password"
                      placeholder="Lösenord"
                      preText=""
                      onTextChanged={setPassword}
                    />
                  </>
                ) : (
                  <div>
                    <div className="tw-flex tw-items-center">
                      <strong>Inloggad som: {userInfo?.username}</strong>
                      {userInfo?.role && getRoleBadge(userInfo.role)}
                    </div>
                    <p className="tw-text-sm tw-text-gray-600 tw-mt-1">{userInfo?.email}</p>
                  </div>
                )}
              </div>

              <div className="tw-flex tw-justify-center tw-gap-2 tw-mt-4 tw-mb-4">
                <button
                  type={isAuthorized ? "button" : "submit"}
                  onClick={isAuthorized ? handleLogInOut : undefined}
                  disabled={loading || (!isAuthorized && (!username.trim() || !password))}
                  className={`tw-px-4 tw-py-2 tw-rounded-lg tw-text-white focus:tw-outline-none focus:tw-ring-2 
                ${loading || (!isAuthorized && (!username.trim() || !password))
                      ? 'tw-bg-gray-400 tw-cursor-not-allowed'
                      : isAuthorized
                        ? 'tw-bg-red-600 hover:tw-bg-red-700 focus:tw-ring-red-400'
                        : 'tw-bg-blue-600 hover:tw-bg-blue-700 focus:tw-ring-blue-400'
                    }`}
                >
                  {isAuthorized ? 'Logga ut' : 'Logga in'}
                </button>
              </div>

              {isAuthorized && (
                <div>
                  <ChangePassword />
                </div>
              )}

              <div className="tw-flex tw-justify-center tw-min-h-[40px]">
                {loading && <BattenburgLoader width={100} left='52%' />}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default LoginOutForm;